/**
 * Mock categories and taxonomies for Exam Management, aligned with PRISM Program Standards.
 */

export interface CategoryNode {
  id: string
  code: string // e.g. "CAPTE 7A.1", "PLO 1.1"
  name: string
  parentId: string | null
  frameworkId: string
  description?: string
  origin: "prism" | "local"
  isPrismSynced: boolean
  lastSyncedAt?: string
}

export interface CategoryFramework {
  id: string
  name: string
  type: "program_competency" | "accreditation_standard" | "course_learning_outcome" | "local_topic"
  origin: "prism" | "local"
  isPrismSynced: boolean
  lastSyncedAt?: string
  nodes: CategoryNode[]
}

export const MOCK_CATEGORIES: CategoryFramework[] = [
  {
    id: "capte_2024",
    name: "CAPTE Standards 2024",
    type: "accreditation_standard",
    origin: "prism",
    isPrismSynced: true,
    lastSyncedAt: "2026-06-24T09:30:00Z",
    nodes: [
      { id: "capte1", code: "Standard 1", name: "Professional Practice", parentId: null, frameworkId: "capte_2024", description: "Demonstrate behaviors consistent with professional practice and ethical codes.", origin: "prism", isPrismSynced: true },
      { id: "capte1_1", code: "CAPTE 1A", name: "Adheres to legal & regulatory standards", parentId: "capte1", frameworkId: "capte_2024", origin: "prism", isPrismSynced: true },
      { id: "capte1_2", code: "CAPTE 1B", name: "Adheres to professional codes of ethics", parentId: "capte1", frameworkId: "capte_2024", origin: "prism", isPrismSynced: true },
      { id: "capte7", code: "Standard 7", name: "Curriculum Content", parentId: null, frameworkId: "capte_2024", description: "Includes foundational and clinical sciences critical to physical therapist practice.", origin: "prism", isPrismSynced: true },
      { id: "capte7_a", code: "CAPTE 7A", name: "Physical Therapy Sciences", parentId: "capte7", frameworkId: "capte_2024", origin: "prism", isPrismSynced: true },
      { id: "capte7_a_1", code: "CAPTE 7A.1", name: "Anatomical & biomechanical principles", parentId: "capte7_a", frameworkId: "capte_2024", origin: "prism", isPrismSynced: true },
      { id: "capte7_a_2", code: "CAPTE 7A.2", name: "Pathology and pathophysiology of systems", parentId: "capte7_a", frameworkId: "capte_2024", origin: "prism", isPrismSynced: true },
      { id: "capte7_c", code: "CAPTE 7C", name: "Clinical Practice Standards", parentId: "capte7", frameworkId: "capte_2024", origin: "prism", isPrismSynced: true },
      { id: "capte7_c_1", code: "CAPTE 7C.1", name: "Performs professional evaluation and screening", parentId: "capte7_c", frameworkId: "capte_2024", origin: "prism", isPrismSynced: true },
      { id: "capte7_c_3", code: "CAPTE 7C.3", name: "Performs physical therapy interventions", parentId: "capte7_c", frameworkId: "capte_2024", origin: "prism", isPrismSynced: true }
    ]
  },
  {
    id: "acote_2023",
    name: "ACOTE Standards 2023",
    type: "accreditation_standard",
    origin: "prism",
    isPrismSynced: true,
    lastSyncedAt: "2026-06-24T09:30:00Z",
    nodes: [
      { id: "acote_a", code: "Standard A", name: "Foundational Content", parentId: null, frameworkId: "acote_2023", description: "Foundational components of occupational therapy education.", origin: "prism", isPrismSynced: true },
      { id: "acote_a_1", code: "ACOTE A.1", name: "Anatomy, physiology, and kinesiology foundations", parentId: "acote_a", frameworkId: "acote_2023", origin: "prism", isPrismSynced: true },
      { id: "acote_b", code: "Standard B", name: "Basic Tenets", parentId: null, frameworkId: "acote_2023", description: "Occupational therapy process and theoretical framework models.", origin: "prism", isPrismSynced: true },
      { id: "acote_b_2", code: "ACOTE B.2", name: "OT Process & Interventions", parentId: "acote_b", frameworkId: "acote_2023", origin: "prism", isPrismSynced: true },
      { id: "acote_b_2_1", code: "ACOTE B.2.1", name: "Formulates occupational profile & evaluation", parentId: "acote_b_2", frameworkId: "acote_2023", origin: "prism", isPrismSynced: true }
    ]
  },
  {
    id: "plo",
    name: "Program Learning Outcomes (PLOs)",
    type: "program_competency",
    origin: "prism",
    isPrismSynced: true,
    lastSyncedAt: "2026-06-24T09:30:00Z",
    nodes: [
      { id: "plo1", code: "PLO 1", name: "Patient Care & Clinical Reasoning", parentId: null, frameworkId: "plo", description: "Derive clinical diagnoses and apply safe, evidence-based treatment plans.", origin: "prism", isPrismSynced: true },
      { id: "plo1_1", code: "PLO 1.1", name: "Formulates accurate clinical diagnoses", parentId: "plo1", frameworkId: "plo", origin: "prism", isPrismSynced: true },
      { id: "plo1_2", code: "PLO 1.2", name: "Develops patient-centered interventions", parentId: "plo1", frameworkId: "plo", origin: "prism", isPrismSynced: true },
      { id: "plo2", code: "PLO 2", name: "Professionalism & Leadership", parentId: null, frameworkId: "plo", description: "Demonstrate ethical leadership and advocacy in health service.", origin: "prism", isPrismSynced: true },
      { id: "plo2_1", code: "PLO 2.1", name: "Adheres to ethical practice guidelines", parentId: "plo2", frameworkId: "plo", origin: "prism", isPrismSynced: true }
    ]
  },
  {
    id: "ilo",
    name: "Institutional Learning Outcomes (ILOs)",
    type: "course_learning_outcome",
    origin: "prism",
    isPrismSynced: true,
    lastSyncedAt: "2026-06-24T09:30:00Z",
    nodes: [
      { id: "ilo1", code: "ILO 1", name: "Communication & Health Literacy", parentId: null, frameworkId: "ilo", description: "Effectively communicate health information to patients and teams.", origin: "prism", isPrismSynced: true },
      { id: "ilo1_1", code: "ILO 1.1", name: "Demonstrates clear written documentation", parentId: "ilo1", frameworkId: "ilo", origin: "prism", isPrismSynced: true },
      { id: "ilo2", code: "ILO 2", name: "Critical Thinking & Inquiry", parentId: null, frameworkId: "ilo", description: "Evaluate clinical literature and translate evidence into practice.", origin: "prism", isPrismSynced: true },
      { id: "ilo2_1", code: "ILO 2.1", name: "Performs database search & literature analysis", parentId: "ilo2", frameworkId: "ilo", origin: "prism", isPrismSynced: true }
    ]
  }
]

// Conflict Resolution mock data
export interface StandardConflict {
  id: string
  courseMeasure: string
  offeringACode: string
  offeringBCode: string
  offeringAStandard: string
  offeringBStandard: string
  status: "unresolved" | "resolved"
  resolvedStandard?: string
  resolutionNote?: string
}

export const MOCK_CONFLICTS: StandardConflict[] = [
  {
    id: "conf1",
    courseMeasure: "Manual Joint Mobilization (Grade III/IV)",
    offeringACode: "CSE-101 (Section A)",
    offeringBCode: "CSE-101 (Section B)",
    offeringAStandard: "CAPTE 7C.3 (Interventions)",
    offeringBStandard: "CAPTE 7A.1 (Biomechanical Principles)",
    status: "unresolved"
  },
  {
    id: "conf2",
    courseMeasure: "HIPAA Confidentiality Policy Check",
    offeringACode: "CSE-101 (Section A)",
    offeringBCode: "CSE-101 (Clinical Labs)",
    offeringAStandard: "CAPTE 1B (Ethics)",
    offeringBStandard: "CAPTE 7C.1 (Evaluation & Screenings)",
    status: "resolved",
    resolvedStandard: "CAPTE 1B (Ethics)",
    resolutionNote: "Ethical standard preferred since questions focus on security and consent rather than clinical diagnostic evaluation."
  }
]

// Auto-Mapping suggestions rules
export interface AutoMappingRule {
  id: string
  keyword: string
  targetCategoryId: string
  targetCategoryCode: string
  targetCategoryName: string
  frameworkId: string
}

export const DEFAULT_MAPPING_RULES: AutoMappingRule[] = [
  { id: "r1", keyword: "gait", targetCategoryId: "capte7_a_1", targetCategoryCode: "CAPTE 7A.1", targetCategoryName: "Anatomical & biomechanical principles", frameworkId: "capte_2024" },
  { id: "r2", keyword: "posture", targetCategoryId: "capte7_a_1", targetCategoryCode: "CAPTE 7A.1", targetCategoryName: "Anatomical & biomechanical principles", frameworkId: "capte_2024" },
  { id: "r3", keyword: "mobilization", targetCategoryId: "capte7_c_3", targetCategoryCode: "CAPTE 7C.3", targetCategoryName: "Performs physical therapy interventions", frameworkId: "capte_2024" },
  { id: "r4", keyword: "hipaa", targetCategoryId: "capte1_2", targetCategoryCode: "CAPTE 1B", targetCategoryName: "Adheres to professional codes of ethics", frameworkId: "capte_2024" },
  { id: "r5", keyword: "confidential", targetCategoryId: "capte1_2", targetCategoryCode: "CAPTE 1B", targetCategoryName: "Adheres to professional codes of ethics", frameworkId: "capte_2024" },
  { id: "r6", keyword: "dermatome", targetCategoryId: "capte7_c_1", targetCategoryCode: "CAPTE 7C.1", targetCategoryName: "Performs professional evaluation and screening", frameworkId: "capte_2024" },
  { id: "r7", keyword: "reflex", targetCategoryId: "capte7_c_1", targetCategoryCode: "CAPTE 7C.1", targetCategoryName: "Performs professional evaluation and screening", frameworkId: "capte_2024" }
]

export function getParentCategoryNodePath(nodeId: string): string {
  for (const f of MOCK_CATEGORIES) {
    const node = f.nodes.find(n => n.id === nodeId)
    if (node) {
      if (node.parentId) {
        const parent = f.nodes.find(n => n.id === node.parentId)
        return parent ? `${parent.code} > ${node.code}` : node.code
      }
      return node.code
    }
  }
  return nodeId
}
